/**
 * Standard Actions configuration for Dawn.
 *
 * Storefront Renderer injects the Shopify Standard Actions bundle
 * (`window.Shopify.actions.{updateCart,openCart,getCart,…}`). This file
 * overrides the bundle's built-in Dawn refresh path with an explicit,
 * in-theme version so forks that change Dawn's cart contract keep
 * working. Remove this file and the built-in defaults take over.
 *
 *   - openCart   — opens <cart-drawer>; falls back to /cart.
 *   - updateCart — after the Storefront API mutation, refreshes the
 *     affected cart sections and publishes `cart-update` so Dawn's
 *     pubsub subscribers react.
 *   - other actions (getCart, etc.) keep the default implementation.
 */

const DAWN_CART_TAGS = ['cart-drawer', 'cart-items', 'cart-drawer-items', 'cart-notification'];
const DAWN_PUBSUB_REFRESHED_SECTIONS = new Set([
  'cart-drawer:cart-drawer',
  'cart-drawer-items:CartDrawer',
  'cart-items:main-cart-items',
]);

function collectCartSections() {
  const sections = new Map();
  for (const el of document.querySelectorAll(DAWN_CART_TAGS.join(','))) {
    let entries;
    try { entries = el.getSectionsToRender?.(); } catch { continue; }
    const tag = el.tagName.toLowerCase();
    for (const entry of entries ?? []) {
      if (DAWN_PUBSUB_REFRESHED_SECTIONS.has(`${tag}:${entry.id}`)) continue;
      const sectionId = entry.section ?? entry.id;
      if (!sectionId || sections.has(sectionId)) continue;
      const root = entry.section ? document.getElementById(entry.id) : document;
      if (!root) continue;
      const mount = entry.selector
        ? (root.querySelector(entry.selector) ?? (entry.section ? root : null))
        : document.getElementById(entry.id);
      if (!mount) continue;
      sections.set(sectionId, { mount, extractSelector: entry.selector || '.shopify-section' });
    }
  }
  return sections;
}

async function refreshDawnCartUI() {
  const sections = collectCartSections();
  const sectionsQuery = sections.size ? `?sections=${[...sections.keys()].join(',')}` : '';
  const cartUrl = (typeof routes !== 'undefined' && routes?.cart_url) || '/cart';
  const url = `${cartUrl}.js${sectionsQuery}`;
  const cartData = await fetch(url, { headers: { Accept: 'application/json' } })
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);
  if (cartData?.sections) {
    for (const [id, { mount, extractSelector }] of sections) {
      const html = cartData.sections[id];
      if (!html) continue;
      const source = new DOMParser().parseFromString(html, 'text/html').querySelector(extractSelector);
      if (source) mount.replaceChildren(...source.childNodes);
    }
  }
  publish(PUB_SUB_EVENTS.cartUpdate, { source: 'external-refresh', cartData: cartData ?? undefined });
}

function initStandardActions() {
  const actions = window.Shopify?.actions;
  if (!actions) return;
  actions.openCart.configure({
    async handler(defaultHandler) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') { drawer.open(); return; }
      return defaultHandler();
    },
  });
  actions.updateCart.configure({
    eventTarget: () => document,
    async handler(defaultHandler) {
      const result = await defaultHandler();
      try { await refreshDawnCartUI(); }
      catch (error) {
        console.error('[Dawn] Standard Actions cart refresh failed; reloading.', error);
        window.location.reload();
      }
      return result;
    },
  });
}

if (window.Shopify?.actions) initStandardActions();
else document.addEventListener('DOMContentLoaded', initStandardActions, { once: true });

/* Velvet cart conversion layer */
(() => {
  const FREE_SHIPPING_CENTS = 7900;
  let timer;
  let rendering = false;

  const money = (cents, currency = 'EUR') =>
    new Intl.NumberFormat('fr-FR', { style: 'currency', currency }).format(cents / 100);

  async function renderVelvetCart() {
    if (rendering) return;
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    rendering = true;
    try {
      const cart = await fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then((response) => response.ok ? response.json() : null);
      if (!cart || cart.item_count === 0) return;

      const header = drawer.querySelector('.drawer__header');
      const footer = drawer.querySelector('.drawer__footer');
      const ctas = drawer.querySelector('.cart__ctas');
      if (!header || !footer) return;

      let progress = drawer.querySelector('.velvet-cart-progress');
      if (!progress) {
        progress = document.createElement('div');
        progress.className = 'velvet-cart-progress';
        header.insertAdjacentElement('afterend', progress);
      }
      const remaining = Math.max(0, FREE_SHIPPING_CENTS - cart.total_price);
      const percent = Math.min(100, Math.floor((cart.total_price / FREE_SHIPPING_CENTS) * 100));
      progress.innerHTML = `
        <div class="velvet-cart-progress__top">
          <span>${remaining > 0 ? `Plus que <strong>${money(remaining, cart.currency || 'EUR')}</strong> pour la livraison offerte` : '<strong>La livraison vous est offerte ✨</strong>'}</span>
          <span>${percent}%</span>
        </div>
        <div class="velvet-cart-progress__track"><span class="velvet-cart-progress__fill" style="width:${percent}%"></span></div>`;

      let offer = drawer.querySelector('.velvet-cart-offer');
      if (!offer) {
        offer = document.createElement('div');
        offer.className = 'velvet-cart-offer';
        footer.insertAdjacentElement('beforebegin', offer);
      }
      const needed = Math.max(0, 3 - cart.item_count);
      offer.classList.toggle('is-active', needed === 0);
      offer.innerHTML = `
        <div class="velvet-cart-offer__icon">${needed === 0 ? '✓' : '✦'}</div>
        <div class="velvet-cart-offer__copy">
          <strong>2 paires achetées = la 3e offerte</strong>
          <span>${needed === 0 ? 'Votre offre est activée dans le panier.' : `Ajoutez encore ${needed} paire${needed > 1 ? 's' : ''} pour profiter de l’offre.`}</span>
        </div>
        <div class="velvet-cart-offer__badge">${needed === 0 ? 'Offre activée' : 'Offre Velvet'}</div>`;

      if (ctas) {
        let trust = footer.querySelector('.velvet-cart-trust');
        if (!trust) {
          trust = document.createElement('div');
          trust.className = 'velvet-cart-trust';
          ctas.insertAdjacentElement('beforebegin', trust);
        }
        trust.innerHTML = '<span>🔒 Paiement<br>sécurisé</span><span>✉ Livraison<br>suivie</span><span>✓ Normes<br>européennes</span>';

        const button = ctas.querySelector('.cart__checkout-button');
        if (button) button.textContent = `Finaliser ma commande • ${money(cart.total_price, cart.currency || 'EUR')}`;

        let secure = footer.querySelector('.velvet-cart-secure');
        if (!secure) {
          secure = document.createElement('p');
          secure.className = 'velvet-cart-secure';
          ctas.insertAdjacentElement('afterend', secure);
        }
        secure.textContent = 'Paiement sécurisé • Commande préparée avec soin';
      }
    } catch (error) {
      console.warn('[Velvet] Cart enhancements unavailable', error);
    } finally {
      rendering = false;
    }
  }

  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(renderVelvetCart, 180);
  }

  document.addEventListener('DOMContentLoaded', () => {
    renderVelvetCart();
    const drawer = document.querySelector('cart-drawer');
    if (drawer) new MutationObserver(schedule).observe(drawer, { childList: true, subtree: true });
  });
  document.addEventListener('change', (event) => {
    if (event.target.closest?.('cart-drawer')) setTimeout(renderVelvetCart, 650);
  });
  document.addEventListener('click', (event) => {
    if (event.target.closest?.('cart-drawer .quantity__button, cart-drawer cart-remove-button')) {
      setTimeout(renderVelvetCart, 800);
    }
  });
})();
/* Velvet dynamic storefront */
(()=>{function product(){const i=document.querySelector('main[data-template="product"] .product__info-container'),f=i?.querySelector('.product-form');if(!f||i.querySelector('.velvet-product-offer'))return;const o=document.createElement('div');o.className='velvet-product-offer';o.innerHTML='<small>✦ OFFRE DE LANCEMENT</small><strong>2 paires achetées, la 3e offerte</strong><p>Ajoutez trois teintes : la moins chère est automatiquement offerte.</p><b>Meilleure offre</b>';f.before(o);const b=document.createElement('div');b.className='velvet-benefits';b.innerHTML='<span>◷<br>Jusqu’à 12 mois</span><span>✓<br>Normes européennes</span><span>✉<br>Livraison suivie</span>';f.after(b)}function reveal(){if(!('IntersectionObserver'in window)||matchMedia('(prefers-reduced-motion: reduce)').matches)return;const ob=new IntersectionObserver(es=>es.forEach(e=>{if(e.isIntersecting){e.target.classList.add('is-visible');ob.unobserve(e.target)}}),{threshold:.08});[...document.querySelectorAll('main .shopify-section')].slice(1).forEach(s=>{s.classList.add('velvet-reveal');ob.observe(s)})}document.addEventListener('DOMContentLoaded',()=>{product();reveal()})})();